import React from 'react';
import { useState } from 'react';
import { ApiRoutes } from 'routes/routeConstants/apiRoutes';
import { generatePath } from 'react-router-dom';
import { deserialize } from 'serializr';
import axiosInstance from 'interceptor/axiosInstance';
import { Company } from 'models/Company/Company.model';
import { User } from 'models/User/User.model';
import { CompanyAddress } from 'models/CompanyAddress/CompanyAddress.model';
import { Role } from 'models/Role/role.model';
import { MyPlan } from 'models/MyPlan/myPlan.model';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';
import { UserApiMessages } from 'shared/components/Accounts/UserDetails/usersuccesmessage';


export interface UserAccount {
  name?: string;
  role?: string | number;
  email?: string;
  phone?: string;
  position?: string;
  association?: string;
  extension?: string;
}

const AccountsService = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [company, setCompany] = useState<Company>();
  const [accountsList, setAccountsList] = useState<User[]>([]);
  const [planDetail, setPlanDetail] = useState<MyPlan>();
  const [companyAddress, setCompanyAddress] = useState<CompanyAddress>();
  const [roleList, setRoleList] = useState<Role[]>([]);

  const fetchCompany = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(ApiRoutes.FETCH_COMPANY);
      const data = deserialize(
        Company,
        response.data['data']['company']
      ) as Company;

      const companyAddressData = deserialize(
        CompanyAddress,
        response.data['data']['company_address']
      );

      setCompanyAddress(companyAddressData);
      setCompany(data);
    } catch (error) {
      console.log('Error fetching company:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async (params?: { search?: string }) => {
    try {
      setLoading(true);
      const response = await axiosInstance(ApiRoutes.FETCH_ACCOUNTS, {
        params,
      });
      const data = deserialize(User, response.data['data']) as User[];
      setAccountsList(data);
    } catch (error) {
      console.log('Error fetching Accounts: ', error);
    } finally {
      setLoading(false);
    }
  };
  const createUserAccount = async (User: UserAccount) => {
    try {
      setButtonLoading(true);
      await axiosInstance.post(ApiRoutes.ADD_USER, User);
      await fetchAccounts();
    } catch (error) {
      console.log('Error creating Account: ', error);
      throw error;
    } finally {
      setButtonLoading(false);
    }
  };

  const updateUserAccount = async (
    id: number | undefined,
    User: UserAccount
  ) => {
    try {
      setButtonLoading(true);
      const url = generatePath(ApiRoutes.UPDATE_USER, { id });
      await axiosInstance.put(url, User);
      await fetchAccounts();
      Notification({
        message: UserApiMessages.USER_UPDATED,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    } catch (error) {
      console.log('Error updating user account', error);
    } finally {
      setButtonLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(ApiRoutes.ROLES);
      const data = deserialize(Role, response.data['data']['role']) as Role[];
      setRoleList(data);
    } catch (error) {
      console.log('Error is ', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (id: number) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.DELETE_USER, { id });
      await axiosInstance.delete(url);
      await fetchAccounts();
      Notification({
        message: 'Successfully deleted the user',
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    } catch (error) {
      console.log('Error deleting user: ', deleteUser);
    } finally {
      setLoading(false);
    }
  };

  //Get plan details
  const getPlanDetails = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(ApiRoutes.GET_PLAN_DETAILS);
      const data = deserialize(MyPlan, res.data.data as MyPlan);
      setPlanDetail(data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  return {
    loading,
    fetchCompany,
    company,
    fetchAccounts,
    accountsList,
    createUserAccount,
    updateUserAccount,
    deleteUser,
    companyAddress,
    fetchRoles,
    getPlanDetails,
    planDetail,
    roleList,
  };
};

export default AccountsService;
